import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import CollecteLoadingMessage from "../components/CollecteLoadingMessage";
import { pinService } from "../services/PinService";
import { userService } from "../services/UserService"
import "../styles/ResetPasswordPopup.css";
import CollecteErrorMessage from "../components/CollecteErrorMessage";

const ResetPasswordPopup: React.FC<{ show: boolean; onClose: () => void }> = ({ show, onClose }) => {
  const [step, setStep] = useState<"email" | "pin" | "newPassword">("email");
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState<string[]>(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pinValidated, setPinValidated] = useState(false);
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (show) {
      setStep("email");
      setEmail("");
      setPin(["", "", "", "", "", ""]);
      setPinValidated(false);
      setPassword("");
      setConfirmPassword("");
      setError("");
      setEmailSent(false);
    }
  }, [show]);

  // === Gestion PIN ===
  const handleChangePin = (index: number, value: string) => {
    if (/^\d?$/.test(value) && !pinValidated) {
      const newPin = [...pin];
      newPin[index] = value;
      setPin(newPin);

      if (value && index < 5) {
        inputsRef.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDownPin = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !pin[index] && index > 0 && !pinValidated) {
      const newPin = [...pin];
      newPin[index - 1] = "";
      setPin(newPin);
      inputsRef.current[index - 1]?.focus();
    }
  };

  useEffect(() => {
    if (!show) return;

    const blockEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("keydown", blockEnter, true);

    return () => {
      document.removeEventListener("keydown", blockEnter, true);
    };
  }, [show]);

  // === Validation PIN ===
  useEffect(() => {
    const verify = async () => {
      if (step === "pin" && !pinValidated) {
        const pinStr = pin.join("");
        if (pinStr.length === 6) {
          try {
            const isValid = await pinService.verifyPin(api, email, pinStr);
            if (isValid) {
              setPinValidated(true);
              setError("");
              setStep("newPassword");
            } else {
              setError("PIN incorrect.");
            }
          } catch (err: any) {
            console.error("Erreur lors de la vérification du PIN :", err);
            setError("Impossible de vérifier le PIN. Réessayez plus tard.");
          }
        }
      }
    };

    verify();
  }, [pin, pinValidated, step, email]);


  const handleSubmitPassword = async () => {
    if (!password || !confirmPassword) {
      setError("Veuillez remplir les deux champs.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const message = await userService.updatePassword(api, email, password);
      onClose();
    } catch (err: any) {
      console.error("Erreur lors de la mise à jour du mot de passe :", err);
      setError(err || "Impossible de mettre à jour le mot de passe. Réessayez plus tard.");
    } finally {
      setLoading(false);
    }
  };


  const handleSendEmail = async () => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setErrorMessage("Veuillez entrer un email valide.");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    setLoading(true);

    try {
      const data = await pinService.createPin(api, email);

      if (data.success) {
        setEmailSent(true);
        setErrorMessage("");

        setTimeout(() => setStep("pin"), 1000);
      } else {
        setErrorMessage(data.message);
        setEmailSent(false);
        setTimeout(() => setErrorMessage(""), 3000);
      }

    } catch (err: any) {
      console.error("Erreur inattendue lors de la création du PIN :", err);
      setErrorMessage(err.message || "Impossible de créer le PIN. Réessayez plus tard.");
      setEmailSent(false);
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = () => {
    setEmailSent(true);
  };

  if (!show) return null;

  return (
    <div className="overlay" onClick={onClose}>
      <CollecteErrorMessage
        visible={!!errorMessage}
        message={errorMessage}
        onClose={() => setErrorMessage("")}
      />

      <div className="popup" onClick={(e) => e.stopPropagation()}>
        {step === "email" && (
          <>
            <h4>Réinitialiser le mot de passe</h4>
            <input
              type="email"
              placeholder="Entrez votre email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-control"
            />
            {error && <p className="error">{error}</p>}
            <div className="button-container">
              <button onClick={onClose}>Annuler</button>
              <button onClick={handleSendEmail}>Envoyer le code</button>
            </div>
            {emailSent && <p className="success">Email envoyé !</p>}
          </>
        )}

        {step === "pin" && (
          <>
            <h4> Entrez le PIN à 6 chiffres envoyé à{" "} {email.length > 15 ? `${email.slice(0, 20)}...` : email} </h4>
            <small>Valide pendant 5 minutes seulement</small>
            <div className="pin-container">
              {pin.map((num, idx) => (
                <input
                  key={idx}
                  type="password"
                  value={num}
                  maxLength={1}
                  onChange={(e) => handleChangePin(idx, e.target.value)}
                  onKeyDown={(e) => handleKeyDownPin(idx, e)}
                  ref={(el) => { inputsRef.current[idx] = el; }}
                  readOnly={pinValidated}
                />
              ))}
            </div>
            {error && <p className="error">{error}</p>}
            <p>
              Vous n'avez pas reçu l'email ?{" "}
              <span className="resend-email" onClick={handleResendEmail}>
                Renvoyer l'email
              </span>
            </p>
          </>
        )}

        {step === "newPassword" && (
          <>
            <h3>Nouveau mot de passe</h3>
            <input
              type="password"
              placeholder="Nouveau mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-control"
            />
            <input
              type="password"
              placeholder="Confirmer le mot de passe"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="form-control"
              style={{ marginTop: "10px" }}
            />
            {error && <p className="error">{error}</p>}
            <div className="button-container">
              <button onClick={onClose}>Annuler</button>
              <button onClick={handleSubmitPassword}>Valider</button>
            </div>
          </>
        )}
      </div>
      {loading && <CollecteLoadingMessage message="Envoi de l’email..." />}
    </div>
  );
};

export default ResetPasswordPopup;
